import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { dailyNormEditDialog as t } from '@/styles/tokens';
import { ModalIndices } from '@/common/i-doc/i-doc.registry';
import { MutedText, NormDialogShell, NormInput, RecommendedBlock, RecommendedValue } from './dailyNormDialogParts';

export type MacroKey = 'proteinG' | 'fatG' | 'carbsG';
export type MacrosGrams = Record<MacroKey, number | null>;

const ROWS: { key: MacroKey; labelKey: string; color: string }[] = [
  { key: 'proteinG', labelKey: 'clientProfile.dailyNorms.norm.protein', color: t.protein },
  { key: 'fatG', labelKey: 'clientProfile.dailyNorms.norm.fat', color: t.fat },
  { key: 'carbsG', labelKey: 'clientProfile.dailyNorms.norm.carbs', color: t.carbs },
];

// Частка добової калорійності: ккал на 1 г (білки / вуглеводи - 4, жири - 9).
const KCAL_PER_G: Record<MacroKey, number> = { proteinG: 4, fatG: 9, carbsG: 4 };

const round1 = (value: number) => Math.round(value * 10) / 10;

/** % від калорій для грамів; null - немає калорій або грамів. */
export const gramsToPercent = (key: MacroKey, grams: number | null, calories: number | null): number | null =>
  grams == null || !calories ? null : Math.round((grams * KCAL_PER_G[key] * 100) / calories);

const percentToGrams = (key: MacroKey, percent: number, calories: number): number =>
  round1((percent * calories) / (100 * KCAL_PER_G[key]));

const toInput = (value: number | null) => (value == null ? '' : String(value));
const parse = (value: string): number | null => (value.trim() === '' || !Number.isFinite(Number(value)) ? null : Number(value));

// Ширина поля: 140 (desktop), 92 (mobile) - за макетами.
const MACRO_INPUT_SX = { width: { xs: 92, sm: 140 }, flexShrink: 0 };

type Fields = Record<MacroKey, { g: string; pct: string }>;

interface MacrosEditDialogProps {
  open: boolean;
  onClose: () => void;
  /** Поточні (ефективні) грами - у поля. */
  grams: MacrosGrams | null;
  /** Ефективна норма калорій - база відсотків. */
  calories: number | null;
  /** Стандартні норми (результат калькуляції) - блок «Рекомендована норма». */
  recommended: { grams: MacrosGrams; calories: number | null } | null;
  /** Рядок, у полі грамів якого фокус при відкритті. */
  focusKey: MacroKey | null;
  onSave: (grams: MacrosGrams) => void;
  saving?: boolean;
  /** Кнопка AMDR - пояснення розрахунку. */
  onFormulaClick?: (label: string) => void;
}

// Редагування норм БЖВ (Figma-макет): одна форма на три картки, фокус - на рядку натиснутої картки. Грами й відсотки
// зв'язані через ефективну норму калорій: змінив одне - перерахувалось інше.
export const MacrosEditDialog: React.FC<MacrosEditDialogProps> = ({
  open, onClose, grams, calories, recommended, focusKey, onSave, saving, onFormulaClick,
}) => {
  const { t: tr } = useTranslation();
  const [fields, setFields] = useState<Fields | null>(null);

  useEffect(() => {
    if (!open || !grams) return;
    setFields(
      Object.fromEntries(
        ROWS.map(({ key }) => [key, { g: toInput(grams[key]), pct: toInput(gramsToPercent(key, grams[key], calories)) }]),
      ) as Fields,
    );
  }, [open, grams, calories]);

  if (!grams || !fields) return null;

  // Друге поле рядка перераховується, лише коли відомі калорії.
  const setField = (key: MacroKey, part: 'g' | 'pct', value: string) =>
    setFields((prev) => {
      if (!prev) return prev;
      const number = parse(value);
      const linked =
        part === 'g'
          ? toInput(gramsToPercent(key, number, calories))
          : number == null || !calories ? '' : toInput(percentToGrams(key, number, calories));
      const row = part === 'g' ? { g: value, pct: calories ? linked : prev[key].pct } : { pct: value, g: calories ? linked : prev[key].g };
      return { ...prev, [key]: row };
    });

  // Сума має бути рівно 100% (розподіл добових калорій). Рахується точно з грамів - без проміжних округлень по рядках
  // (інакше коректні грами давали б 99 / 101%); перевірка - за округленою (показаною) сумою. Без калорій відсотків
  // нема - перевірка вимкнена, інакше грами не зберегти взагалі.
  const exactPct = (key: MacroKey) => {
    const g = parse(fields[key].g);
    return g != null && calories ? (g * KCAL_PER_G[key] * 100) / calories : parse(fields[key].pct) ?? 0;
  };
  const totalPct = Math.round(ROWS.reduce((sum, { key }) => sum + exactPct(key), 0));
  const totalError = !calories ? null : totalPct > 100 ? 'totalOver' : totalPct < 100 ? 'totalUnder' : null;

  return (
    <NormDialogShell
      open={open}
      onClose={onClose}
      title={tr('clientProfile.dailyNorms.edit.title', { name: tr('clientProfile.dailyNorms.normName.macros') })}
      iDoc={ModalIndices.DAILY_NORM_MACROS_EDIT}
      saving={saving}
      saveDisabled={totalError != null}
      onSave={() => onSave({ proteinG: parse(fields.proteinG.g), fatG: parse(fields.fatG.g), carbsG: parse(fields.carbsG.g) })}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <MutedText>{tr('clientProfile.dailyNorms.edit.macrosIntro')}</MutedText>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {ROWS.map((r) => (
            <Box key={r.key} sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Typography noWrap sx={{ flexGrow: 1, minWidth: 0, fontSize: 16, lineHeight: '19px', color: t.text }}>{tr(r.labelKey)}</Typography>
              <NormInput
                unit={tr('clientProfile.dailyNorms.unit.g')}
                autoFocus={r.key === focusKey}
                value={fields[r.key].g}
                onChange={(e) => setField(r.key, 'g', e.target.value)}
                sx={MACRO_INPUT_SX}
              />
              <NormInput
                unit="%"
                value={fields[r.key].pct}
                onChange={(e) => setField(r.key, 'pct', e.target.value)}
                sx={MACRO_INPUT_SX}
              />
            </Box>
          ))}
        </Box>

        {totalError ? (
          // Понад 100% - помилка (червоним); менше - ще не розподілено (синім, скільки лишилось).
          <Typography
            sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 600, color: totalError === 'totalOver' ? 'error.main' : t.accent }}
          >
            {tr(`clientProfile.dailyNorms.edit.${totalError}`, { value: totalPct, rest: 100 - totalPct })}
          </Typography>
        ) : (
          <MutedText>{tr('clientProfile.dailyNorms.edit.total', { value: totalPct })}</MutedText>
        )}
      </Box>

      {recommended && ROWS.some(({ key }) => recommended.grams[key] != null) && (
        <RecommendedBlock hint={tr('clientProfile.dailyNorms.edit.formulaHint', { formula: 'AMDR' })} formulaLabels={['AMDR']} onFormulaClick={onFormulaClick}>
          {ROWS.map((r) => {
            const value = recommended.grams[r.key];
            const pct = gramsToPercent(r.key, value, recommended.calories);
            return (
              <RecommendedValue key={r.key} color={r.color} caption={tr(r.labelKey)}>
                {value == null ? '—' : `${value} ${tr('clientProfile.dailyNorms.unit.g')}${pct == null ? '' : ` (${pct}%)`}`}
              </RecommendedValue>
            );
          })}
        </RecommendedBlock>
      )}
    </NormDialogShell>
  );
};
